import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, MapPin, Home, Store, MoreHorizontal } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HINT_OPTIONS = [
  { label: 'Your House', icon: Home, example: 'e.g. Flat 301, Tower B' },
  { label: 'Apartment Shops', icon: Store, example: 'e.g. Shop #4, Ground Floor' },
  { label: 'Others', icon: MoreHorizontal, example: 'e.g. Gate 2, Security Cabin' },
];

const VendorPickupPoints = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    landmark: '',
    latitude: '',
    longitude: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vendor/pickup-points`, {
        withCredentials: true,
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch pickup points:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        address: item.address || '',
        landmark: item.landmark || '',
        latitude: item.latitude ?? '',
        longitude: item.longitude ?? '',
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', address: '', landmark: '', latitude: '', longitude: '' });
    }
    setDialogOpen(true);
  };

  const handleHintClick = (hint) => {
    setFormData({ ...formData, name: hint });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        address: formData.address || null,
        landmark: formData.landmark || null,
        latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
      };

      if (editingItem) {
        await axios.put(`${API_URL}/api/vendor/pickup-points/${editingItem.id}`, payload, {
          withCredentials: true,
        });
      } else {
        await axios.post(`${API_URL}/api/vendor/pickup-points`, payload, {
          withCredentials: true,
        });
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pickup point?')) return;
    try {
      await axios.delete(`${API_URL}/api/vendor/pickup-points/${id}`, {
        withCredentials: true,
      });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  return (
    <div data-testid="vendor-pickup-points-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Pickup Points</h1>
          <p className="font-body text-text-secondary">Manage pickup locations for your customers</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
          data-testid="add-pickup-point-btn"
        >
          <Plus size={18} />
          Add Pickup Point
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-text-muted">
            <MapPin size={48} className="mb-4 opacity-40" />
            <p className="font-body">No pickup points yet</p>
            <Button
              variant="link"
              onClick={() => handleOpenDialog()}
              className="text-brand-primary"
            >
              Add your first pickup point
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-background-secondary">
                <TableHead className="font-heading font-semibold">Name</TableHead>
                <TableHead className="font-heading font-semibold">Address</TableHead>
                <TableHead className="font-heading font-semibold">Landmark</TableHead>
                <TableHead className="font-heading font-semibold">Coordinates</TableHead>
                <TableHead className="font-heading font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-body font-medium">{item.name}</TableCell>
                  <TableCell className="font-body text-text-secondary">{item.address || '-'}</TableCell>
                  <TableCell className="font-body text-text-secondary">{item.landmark || '-'}</TableCell>
                  <TableCell className="font-body text-text-secondary text-sm">
                    {item.latitude != null && item.longitude != null
                      ? `${item.latitude}, ${item.longitude}`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(item)}
                        className="text-text-secondary hover:text-brand-primary"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-text-secondary hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit' : 'Add'} Pickup Point
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="pp-name" className="font-body font-medium">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="pp-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Your House (C1102), Apartment Shops, Others"
                className="rounded-xl"
                data-testid="pickup-name-input"
              />
              <p className="text-xs text-text-muted">Eg. Your House (C1102), Apartment Shops, Others</p>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="pp-address" className="font-body font-medium">Address</Label>
              <Input
                id="pp-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address of the pickup point"
                className="rounded-xl"
                data-testid="pickup-address-input"
              />
            </div>

            {/* Landmark */}
            <div className="space-y-2">
              <Label htmlFor="pp-landmark" className="font-body font-medium">Landmark</Label>
              <Input
                id="pp-landmark"
                value={formData.landmark}
                onChange={(e) => setFormData({ ...formData, landmark: e.target.value })}
                placeholder="Nearby landmark for easy identification"
                className="rounded-xl"
                data-testid="pickup-landmark-input"
              />
            </div>

            {/* Lat/Lng */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="pp-lat" className="font-body font-medium">Latitude</Label>
                <Input
                  id="pp-lat"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="e.g. 12.9716"
                  className="rounded-xl"
                  data-testid="pickup-latitude-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pp-lng" className="font-body font-medium">Longitude</Label>
                <Input
                  id="pp-lng"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="e.g. 77.5946"
                  className="rounded-xl"
                  data-testid="pickup-longitude-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.name}
              className="bg-brand-primary hover:bg-brand-primary-hover rounded-xl"
              data-testid="pickup-save-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorPickupPoints;
